import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Circle, MapContainer, Polygon, TileLayer } from 'react-leaflet';
import { Box, MenuItem, Typography, useTheme } from '@material-ui/core';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { LatLngExpression, LatLngLiteral, Map } from 'leaflet';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import { MapMarker, TimeMember, TrackEvent, TrackEventType } from '@vyce/core/src/types';
import { useQuery } from '@vyce/core/src/hooks';
import { getMapViewEventsRequest } from '@vyce/core/src/api/time';
import markerLight from '@vyce/core/src/assets/svg/marker.svg';
import markerDark from '@vyce/core/src/assets/svg/marker-dark-mode.svg';
import markerGrey from '@vyce/core/src/assets/svg/marker-grey.svg';
import { useInterval } from '@vyce/core/src/hooks/useInterval';
import { SITE_RADIUS } from '@vyce/core/src/modules/timeModule/constants';
import { TimeModuleContext } from '@vyce/core/src/contexts';
import { useTabFocus } from '@vyce/core/src/hooks/useTabFocus';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { WidgetWrapper, WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';

import { LONDON_POSITION, MAP_BOX_ATTRIBUTION } from '../../map/constants';
import { TimeModuleMarker } from '../../map/TimeModuleMarker';
import { AppMarker } from '../../map/AppMarker';
import { useMapStyle } from '../../map/hooks/useMapStyle';
import { AppSelect } from '../../../components/inputs/AppSelect';
import { useStyles } from '../../widgets/LocationUsersWidget/styles';
import { AppSearchInput } from '../../../components/inputs/AppSearchInput';

interface Props {
  selectedCompanyId: string;
}

interface ITimeModuleMarker extends MapMarker {
  eventTime?: string;
  time?: string;
}

const ZOOM = 9;
const mapContainerStyles = { height: '100%', width: '100%', borderRadius: '15px' };
const containerStyles = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
};

export const LiveTimeLocationMap = ({ selectedCompanyId, ...rest }: Props & WidgetWrapperProps) => {
  const classes = useStyles();
  const [search, setSearch] = useState<string>('');
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(rest.onForbidden);
  const { mapStyleId, tileLayerUrl } = useMapStyle();
  const [markers, setMarkers] = useState<ITimeModuleMarker[]>([]);
  const { isTabFocused } = useTabFocus();
  const theme = useTheme();
  const query = useQuery();
  const siteId = query.get('site_id');
  const { locations } = useContext(TimeModuleContext);
  const mapRef = useRef<Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedEventType, setSelectedEventType] = useState<TrackEventType>(TrackEventType.LAST_EVENT);
  const isMounted = useIsMounted();

  const searchedMarkers = useMemo(() => {
    return markers.filter(marker => marker.name.toLowerCase().includes(search.toLowerCase()));
  }, [markers, search]);

  const locationsToDisplay = useMemo(
    () => locations.filter(loc => (siteId ? siteId === loc.uuid : true)),
    [locations, siteId]
  );

  const locationsWithCircleArea = useMemo(
    () => locationsToDisplay.filter(loc => !!loc.center && !loc.polygon),
    [locationsToDisplay, siteId]
  );

  const locationsWithPolygon = useMemo(
    () => locationsToDisplay.filter(loc => !!loc.center && !!loc.polygon),
    [locationsToDisplay, siteId]
  );

  const center = useMemo(() => {
    const centerLoc = locationsToDisplay.find(loc => !!loc.center);
    if (centerLoc) {
      return centerLoc.center;
    }
    return LONDON_POSITION;
  }, [locationsToDisplay]);

  const getWorkersPositions = async () => {
    if (!selectedCompanyId || !isTabFocused) {
      return;
    }
    const eventType = selectedEventType || TrackEventType.CLOCK_IN;
    try {
      const res = await getMapViewEventsRequest({
        companyId: selectedCompanyId,
        siteId,
        eventType,
      });
      const markers: ITimeModuleMarker[] = res.data.items.map(
        (item: { worker: TimeMember; event: TrackEvent }) => ({
          lat: item.event?.lon || 0,
          lon: item.event?.lat || 0,
          name: `${item.worker?.first_name} ${item.worker?.last_name}`,
          time: dayjs(item.event.timestamp).format('HH:mm'),
          eventTime: item.event.timestamp,
          picture: item.worker?.avatar
            ? item.worker?.avatar
            : item.event?.event_type === TrackEventType.CLOCK_OUT
            ? markerGrey
            : theme.palette.type === 'dark'
            ? markerDark
            : markerLight,
          id: item.worker?.uuid,
        })
      );
      if (!isMounted.current) return;
      setMarkers(markers.filter(marker => marker.lat && marker.lon));
    } catch (e) {
      handleWidgetError(e);
    }
  };

  const handleEventTypeChange = (event: any) => {
    setSelectedEventType(event.target.value);
  };

  useInterval(getWorkersPositions, 15000);

  useEffect(() => {
    if (selectedCompanyId) {
      getWorkersPositions();
    }
  }, [selectedCompanyId, siteId]);

  useEffect(() => {
    getWorkersPositions();
  }, [selectedEventType]);

  useEffect(() => {
    const container = containerRef.current;

    const debouncedCallback = debounce(() => {
      if (mapRef?.current) {
        mapRef.current.invalidateSize();
      }
    }, 100);

    const resizeObserver = new ResizeObserver(debouncedCallback);

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      debouncedCallback.cancel();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <WidgetWrapper {...rest}>
      {locationsToDisplay.length ? (
        <div ref={containerRef} style={containerStyles}>
          <Box display="flex" marginBottom={2} gridGap={12} justifyContent="space-between">
            <AppSearchInput
              onChange={e => setSearch(e.target.value)}
              isSmall
              smallRounding
              isBorder
              expanded={true}
            />

            <Box width="270px">
              <AppSelect
                onChange={handleEventTypeChange}
                value={selectedEventType}
                labelId="select-event-type"
                fullWidth
                className={classes.selector}
              >
                {[
                  { value: TrackEventType.LAST_EVENT, label: 'Last Known Location' },
                  { value: TrackEventType.CLOCK_IN, label: 'Show Today’s Clock In’s' },
                  { value: TrackEventType.CLOCK_OUT, label: 'Show Today’s Clock Out’s' },
                ].map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </AppSelect>
            </Box>
          </Box>

          <MapContainer
            key={mapStyleId}
            scrollWheelZoom={false}
            doubleClickZoom
            style={mapContainerStyles}
            center={center}
            zoom={ZOOM}
            whenCreated={map => {
              mapRef.current = map;
              // Guard the deferred resize: the widget can unmount (map destroyed) before it fires,
              // which would crash with "_leaflet_pos of undefined".
              setTimeout(() => {
                if (isMounted.current && mapRef.current) {
                  mapRef.current.invalidateSize();
                }
              }, 100);
            }}
          >
            <TileLayer url={tileLayerUrl} attribution={MAP_BOX_ATTRIBUTION} />
            <MarkerClusterGroup chunkedLoading>
              {searchedMarkers.map(marker => (
                <TimeModuleMarker
                  picture={marker.picture}
                  workerName={marker.name}
                  key={marker.id}
                  time={`${
                    selectedEventType === TrackEventType.CLOCK_IN
                      ? 'Clocked In at'
                      : selectedEventType === TrackEventType.CLOCK_OUT
                      ? 'Clocked Out at'
                      : 'Last seen at'
                  } ${marker.time}`}
                  position={[marker.lat as number, marker.lon as number]}
                />
              ))}
            </MarkerClusterGroup>
            <>
              {locationsWithCircleArea.map(loc => (
                <Box key={loc.uuid}>
                  <Circle
                    center={loc.center as LatLngLiteral}
                    pathOptions={{ color: theme.palette.primary.main }}
                    radius={SITE_RADIUS}
                  />
                  <AppMarker isMe popupText={loc?.name} position={loc.center as LatLngLiteral} />
                </Box>
              ))}
              {locationsWithPolygon.map(loc => (
                <Box key={loc.uuid}>
                  <Polygon
                    pathOptions={{ color: theme.palette.primary.main }}
                    positions={
                      loc.polygon?.coordinates[0].map(item => ({
                        lat: item[1],
                        lng: item[0],
                      })) as LatLngExpression[]
                    }
                  />
                  <AppMarker isMe popupText={loc?.name} position={loc.center as LatLngLiteral} />
                </Box>
              ))}
            </>
          </MapContainer>
        </div>
      ) : (
        <Box display="flex" height="100%" justifyContent="center" alignItems="center">
          <Typography>No Locations found...</Typography>
        </Box>
      )}
    </WidgetWrapper>
  );
};
